/**
 * Card files. Uploads go one file at a time with progress; the list is the server's copy
 * (`queryKeys.cards.attachments`). Anything that changes the files also changes the card
 * (attachment count, cover), so the board preview and the card are refreshed too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api, apiUpload, queryKeys } from '@/shared/api';

import { type Attachment, MAX_UPLOAD_BYTES, type UploadItem } from './types';

export function useAttachments(cardId: string) {
  return useQuery({
    queryKey: queryKeys.cards.attachments(cardId),
    queryFn: ({ signal }) => api.get<Attachment[]>(`/v1/cards/${cardId}/attachments`, { signal }),
  });
}

function useAfterFilesChange(cardId: string, boardId: string) {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: queryKeys.cards.attachments(cardId) });
    void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
    void client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
    void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
  }, [client, cardId, boardId]);
}

export function useDeleteAttachment(cardId: string, boardId: string) {
  const refresh = useAfterFilesChange(cardId, boardId);
  return useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/v1/attachments/${attachmentId}`),
    onSuccess: refresh,
  });
}

let nextKey = 0;

export interface Uploader {
  items: UploadItem[];
  /** Start uploading files (each is checked against the size limit first). */
  add: (files: readonly File[]) => void;
  /** Cancel a running upload or dismiss a finished/failed row. */
  remove: (key: string) => void;
  /** Retry a failed upload. */
  retry: (key: string) => void;
}

/**
 * Upload queue of one card dialog. Files go in parallel (at most three at a time would be
 * overkill for a card: each is a single request), progress comes from the request itself.
 */
export function useAttachmentUploader(cardId: string, boardId: string): Uploader {
  const [items, setItems] = useState<UploadItem[]>([]);
  const controllers = useRef(new Map<string, AbortController>());
  const files = useRef(new Map<string, File>());
  const refresh = useAfterFilesChange(cardId, boardId);

  useEffect(() => {
    const running = controllers.current;
    return () => {
      for (const controller of running.values()) controller.abort();
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...change } : item)),
    );
  }, []);

  const start = useCallback(
    (key: string, file: File) => {
      const controller = new AbortController();
      controllers.current.set(key, controller);
      patch(key, { status: 'uploading', progress: 0, error: undefined });
      apiUpload<Attachment>(`/v1/cards/${cardId}/attachments`, file, {
        filename: file.name || 'file',
        signal: controller.signal,
        onProgress: (loaded, total) => {
          patch(key, { progress: total > 0 ? loaded / total : 0 });
        },
      }).then(
        () => {
          controllers.current.delete(key);
          files.current.delete(key);
          patch(key, { status: 'done', progress: 1 });
          refresh();
          // A finished row disappears by itself.
          window.setTimeout(() => {
            setItems((current) => current.filter((item) => item.key !== key));
          }, 1500);
        },
        (error: unknown) => {
          controllers.current.delete(key);
          if (error instanceof DOMException && error.name === 'AbortError') return;
          patch(key, { status: 'error', error });
        },
      );
    },
    [cardId, patch, refresh],
  );

  const add = useCallback(
    (selected: readonly File[]) => {
      for (const file of selected) {
        nextKey += 1;
        const key = `upload-${nextKey}`;
        files.current.set(key, file);
        const tooLarge = file.size > MAX_UPLOAD_BYTES;
        setItems((current) => [
          ...current,
          {
            key,
            name: file.name || 'file',
            size: file.size,
            progress: 0,
            status: tooLarge ? 'error' : 'uploading',
            ...(tooLarge && { error: new ApiError(413, 'MEDIA_TOO_LARGE', 'File is too large') }),
          },
        ]);
        if (!tooLarge) start(key, file);
      }
    },
    [start],
  );

  const remove = useCallback((key: string) => {
    controllers.current.get(key)?.abort();
    controllers.current.delete(key);
    files.current.delete(key);
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  const retry = useCallback(
    (key: string) => {
      const file = files.current.get(key);
      if (file) start(key, file);
    },
    [start],
  );

  return { items, add, remove, retry };
}
